import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@/services/api';
import { User } from '@/types';

// SSO provisioning falls back to the email address when Azure sends no name
// claim, and certificates snapshot users.name — so "no real name yet" means
// blank OR email-like, mirroring resolveDisplayName on the server.
export const staffNameNeedsSetup = (user: User | null): boolean => {
  if (!user) return false;
  const name = (user.name || '').trim();
  return !name || name.includes('@');
};

interface StaffNameGateProps {
  user: User;
  onSaved: (name: string) => void;
  onSkip: () => void;
  onLogout: () => void;
}

export const StaffNameGate: React.FC<StaffNameGateProps> = ({ user, onSaved, onSkip, onLogout }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter your full name.');
      return;
    }
    if (trimmed.includes('@')) {
      setError('Please enter your full name, not an email address.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const result = await api.updateOwnName(user.id, trimmed);
      onSaved(result.name);
    } catch (e: any) {
      setError(e?.message || 'Failed to save your name.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full relative border border-slate-200">
        <h3 className="font-bold text-lg text-slate-800 mb-1">Set Your Full Name</h3>
        <p className="text-xs text-slate-500 mb-4">
          Your account doesn't have a full name yet. It appears on certificates and records, so
          please set it before continuing.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
              Full Name
            </label>
            <input
              type="text"
              autoComplete="name"
              autoFocus
              className="w-full rounded-lg p-2 text-sm border border-slate-300 focus:ring-teal-500 focus:border-teal-500"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSaving) handleSave();
              }}
              placeholder="e.g. Jane Smith"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Signed in as <span className="font-bold">{user.email}</span>
            </p>
          </div>
          {error && (
            <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
              {error}
            </p>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              'Save Name'
            )}
          </button>
          <div className="flex items-center justify-between">
            <button
              onClick={onSkip}
              disabled={isSaving}
              className="text-slate-500 text-xs font-bold hover:text-slate-700"
            >
              Skip for now
            </button>
            <button
              onClick={onLogout}
              disabled={isSaving}
              className="text-slate-500 text-xs font-bold hover:text-slate-700"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
